import { Col, Row } from 'antd';
import React, { FC, useCallback } from 'react';
import { WidgetType } from 'api/dashboard/widget';
import { ReactComponent as ArrSVG } from 'assets/svgs/arr.svg';
import { ReactComponent as BarChartThickSVG } from 'assets/svgs/bar-chart-thick.svg';
import { ReactComponent as BarChartSVG } from 'assets/svgs/bar-chart.svg';
import { ReactComponent as GroupedBarChartSVG } from 'assets/svgs/grouped-bar-chart.svg';
import { ReactComponent as NumberWidgetSVG } from 'assets/svgs/number-widget.svg';
import { ReactComponent as StackedBarChartSVG } from 'assets/svgs/stacked-bar-chart.svg';
import {
  CustomCardIcon,
  CustomChartIcon,
} from 'components/organisms/dashboard/components/custom-widget-icons';
import { Translate, TranslationKey, useTranslate } from 'hooks/use-translation';
import { unreachable } from 'utils/unreachable';
import { WidgetTypeSelect } from './widget-type';

const getChartSvg = (type: WidgetType) => {
  switch (type) {
    case WidgetType.CustomCard:
      return <CustomCardIcon />;
    case WidgetType.CustomChart:
      return <CustomChartIcon />;
    case WidgetType.ProjectByStatus:
      return <BarChartSVG />;
    case WidgetType.InferredProgressByProjectOwner:
      return <BarChartThickSVG />;
    case WidgetType.InferredProgressByTemplate:
      return <BarChartThickSVG />;
    case WidgetType.ProjectByInferredProgress:
      return <BarChartThickSVG />;
    case WidgetType.StatusByPhase:
      return <StackedBarChartSVG />;
    case WidgetType.TaskByStatus:
      return <GroupedBarChartSVG />;
    case WidgetType.ProjectCount:
      return <NumberWidgetSVG />;
    case WidgetType.ARR:
      return <ArrSVG />;
    default:
      return unreachable(type);
  }
};

const getDescription = (type: WidgetType, translate: Translate) =>
  translate(
    `dashboard.widget_type.${type.toLowerCase()}.description` as TranslationKey,
  );

const widgetTypeToName = (
  widgetType: WidgetType,
  translate: Translate,
): string => {
  return translate(
    `dashboard.widget_type.${widgetType.toLowerCase()}.title` as TranslationKey,
  );
};

type UseWidgetTypeToName = () => (widgetType: WidgetType) => string;

export const useWidgetTypeToName: UseWidgetTypeToName = () => {
  const translate = useTranslate();

  return useCallback(
    (widgetType: WidgetType) => {
      return widgetTypeToName(widgetType, translate);
    },
    [translate],
  );
};

interface StepOneProps {
  selectedWidgetType: WidgetType;
  setSelectedWidgetType: (latest: WidgetType) => void;
}
export const StepOne: FC<StepOneProps> = ({
  selectedWidgetType,
  setSelectedWidgetType,
}) => {
  const translate = useTranslate();

  return (
    <Row gutter={[16, 16]}>
      {Object.values(WidgetType).map((type) => (
        <Col span={12} key={type}>
          <WidgetTypeSelect
            title={
              ![WidgetType.CustomCard, WidgetType.CustomChart].includes(
                type,
              ) ? (
                <strong>{widgetTypeToName(type, translate)}</strong>
              ) : (
                ''
              )
            }
            value={type}
            description={getDescription(type, translate)}
            icon={getChartSvg(type as WidgetType)}
            selected={selectedWidgetType === type}
            onClick={setSelectedWidgetType}
          />
        </Col>
      ))}
    </Row>
  );
};
